import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import { Breadcrumb } from "../../../../general/modules/commons/breadcrumb/breadcrumb";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderTitle,
} from "../../../../general/modules/commons/layout/layout";
import { logout } from "../../../../auth/infrastructure/slices/authSlice/authSlice";
import { useTranslation } from "react-i18next";
import { Content } from "../../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../../general/modules/commons/content/contentHeader";
import useCrudForm from "../../../../../hooks/useCrudForm";
import { Link, useParams } from "react-router-dom";
import { positionFormSchema } from "../../domain/schemas/positionSchema";
import { PositionMutateFormTemplate } from "../commons/templates/positionMutateFormTemplate";
import { PositionModel } from "../../domain/models/positionModel";
import { Button, Form } from "react-bootstrap";
import { EntitySelectField } from "../../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { shelvingUnitNameSchema } from "../../domain/schemas/positionLabelSchema";
import { useState } from "react";
import { RequiredAsterisks } from "../../../../general/modules/commons/Asterisks/requiredAsterisks";
import { warehouseNameSchema } from "../../../path/domain/schemas/pathSchema";
import useCrudFilters from "../../../../../hooks/useCrudFilters";

export const PositionMutatePage = () => {
  const { t } = useTranslation("common");

  const { id } = useParams();

  const dispatch = useAppDispatch();

  const model = useAppSelector((state) => state.crudMutate)
    .model as PositionModel & {
    shelvingUnit?: {
      id: string;
      path?: {
        id: string;
        warehouse?: {
          id: string;
        };
      };
    };
  };

  const [shelvingUnitId, setShelvingUnitId] = useState<string>(
    model?.shelvingUnit?.id ?? "",
  );

  const [warehouseId, setWarehouseId] = useState<string>(
    model?.shelvingUnit?.path?.warehouse?.id ?? "",
  );

  const shelvingUnitFilters = useCrudFilters({
    searchBy: "SearchByRootShelvingUnit",
    fields: {},
  });

  const title = model.id
    ? t("position.mutate.edit", { name: model.label })
    : t("position.mutate.create");

  const crud = useCrudForm({
    schema: positionFormSchema,
    prefix: "positions",
    createMutation: "upsertPosition",
    updateMutation: "upsertPosition",
    deleteMutation: "deletePosition",
    id: id ? parseInt(id) : undefined,
    editUrl: "/positions/edit/:id",
    formTemplate: PositionMutateFormTemplate,
    ignoreFields: ["label"],
    mutationExclude: ["shelvingUnit", "sticker"],
    queryExclude: ["shelving_unit_id"],
    onBeforeSubmit: (values) => {
      return {
        ...values,
        shelving_unit_id: shelvingUnitId,
      };
    },
  });

  if (Object.keys(model).length < 1 && id) return <></>;

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title} />
          <Breadcrumb></Breadcrumb>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content>
          <ContentHeader title={t("Position")} className="mb-3" />
          {crud.form}

          <Form.Group className="mb-3">
            <Form.Label>
              {t("Warehouse")} <RequiredAsterisks />
            </Form.Label>
            <EntitySelectField
              prefix="warehouses"
              entity="Warehouse"
              labelPrefix={"name"}
              id={"warehouses.id"}
              key={"warehouses.id"}
              name={"warehouses.id"}
              value={
                warehouseId === ""
                  ? `${model?.shelvingUnit?.path?.warehouse?.id}`
                  : warehouseId
              }
              onChange={(option) => {
                if (option == null) {
                  shelvingUnitFilters.removeFilter("path.warehouse.id");
                  return;
                }
                const { value } = option as { value: string };
                setWarehouseId(value);
                shelvingUnitFilters.updateFilter("path.warehouse.id", value);
              }}
              schema={warehouseNameSchema}
              limit={100}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t("Shelving unit")} <RequiredAsterisks />
            </Form.Label>
            <EntitySelectField
              entity="ShelvingUnit"
              prefix="shelvingUnits"
              labelPrefix={"identifier"}
              isDisabled={!warehouseId}
              id={"shelvingUnits.id"}
              key={"shelvingUnits.id"}
              name={"shelvingUnits.id"}
              filters={shelvingUnitFilters.filters}
              value={
                shelvingUnitId === ""
                  ? `${model?.shelvingUnit?.id}`
                  : shelvingUnitId
              }
              onChange={(option) => {
                if (option === null) return;
                setShelvingUnitId((option as { value: string }).value);
              }}
              schema={shelvingUnitNameSchema}
              limit={100}
            />
          </Form.Group>
        </Content>
        <Content md={4}>
          {crud.manageHeader}
          {model?.sticker?.pdfUrl && (
            <Link className="mt-2" to={model.sticker.pdfUrl} target={"_blank"}>
              <Button style={{ width: "100%" }}>{t("sticker.open")}</Button>
            </Link>
          )}
          {crud.manage}
        </Content>
      </LayoutContent>
    </Layout>
  );
};
